import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { MdAccountCircle } from "react-icons/md";
import { LuHistory } from "react-icons/lu";
import { BiSolidReport, BiSolidCalendarStar, BiTask } from "react-icons/bi";
import { RiDashboardFill, RiBook2Line } from "react-icons/ri";
import { TbReport, TbReportSearch } from "react-icons/tb";
import { HiWrenchScrewdriver, HiAcademicCap, HiMiniUserGroup } from "react-icons/hi2";
import { IoChatbubbleEllipsesOutline } from "react-icons/io5";
import { LuFileBadge2, LuBookPlus, LuLibraryBig } from "react-icons/lu";
import { AiOutlineFileProtect, AiTwotoneContainer } from "react-icons/ai";
import { IoIosSpeedometer } from "react-icons/io";
import { MdOutlinePrivacyTip } from "react-icons/md";
import { ImSearch } from "react-icons/im";
import { PiChalkboardTeacherFill, PiStudentFill } from "react-icons/pi";
import { FiBell, FiLayers, FiLogOut, FiMoon, FiSettings, FiSliders, FiSun, FiChevronsLeft, FiX, FiInbox, FiAward } from 'react-icons/fi';
import { FaRegCalendarPlus, FaCalendarAlt, FaUniversity, FaCogs, FaChalkboard } from 'react-icons/fa';
import { useModal } from '../../../context/ModalContext';
import { ROLES } from '../../../constants/roles';
import api from '../../../services/api';
import './Sidebar.css';

const Sidebar = ({
    onLogout,
    userName,
    userRole = ROLES.ADMIN,
    isOpen = false,
    onClose = () => { },
    isCollapsed = false,
    toggleCollapse = () => { }
}) => {
    const { theme, toggleTheme } = useTheme();
    const { confirm } = useModal();
    const navigate = useNavigate();

    const handleLogoutClick = async () => {
        const yes = await confirm({
            title: 'Logout Session?',
            message: 'Are you sure you want to log out? Any unsaved progress may be lost.',
            confirmLabel: 'Logout',
            variant: 'danger',
            icon: <FiLogOut />
        });
        if (yes) onLogout();
    };

    const [platformName, setPlatformName] = React.useState('EduSync');
    const [searchQuery, setSearchQuery] = React.useState('');
    const searchInputRef = React.useRef(null);

    React.useEffect(() => {
        const fetchInfo = async () => {
            try {
                const res = await api.getPublicInfo();
                if (res.data?.success) setPlatformName(res.data.platformName);
            } catch (err) {
                console.error('Failed to fetch platform info', err);
            }
        };
        fetchInfo();
    }, []);

    const formattedRole = userRole
        ? `${userRole.charAt(0).toUpperCase()}${userRole.slice(1)}`
        : ROLES.ADMIN;

    const activeRole = formattedRole === ROLES.ADMIN ? ROLES.ADMIN : ROLES.TEACHER;

    const navGroups = [
        {
            title: 'Overview',
            allowedRoles: [ROLES.ADMIN, ROLES.TEACHER],
            items: [
                { path: '/dashboard', label: 'Dashboard', icon: <RiDashboardFill /> },
                { path: '/instructions', label: 'Instructions', icon: <RiBook2Line /> }
            ],
            // dimensions: { size: 24 },
            // dimensions: { height: 4, width: 4 },
            icon: <IoIosSpeedometer />
        },
        {
            title: 'Academic Setup',
            allowedRoles: [ROLES.ADMIN],
            items: [
                { path: '/teachers', label: 'Teachers', icon: <PiChalkboardTeacherFill /> },
                { path: '/students', label: 'Students', icon: <PiStudentFill /> },
                { path: '/subjects', label: 'Subjects', icon: <LuLibraryBig /> },
                { path: '/sections', label: 'Sections', icon: <HiMiniUserGroup /> },
                { path: '/constraints', label: 'Constraints', icon: <FiSliders /> },
            ],
            icon: <HiAcademicCap />
        },
        {
            title: 'Timetable',
            allowedRoles: [ROLES.ADMIN, ROLES.TEACHER],
            items: [
                { path: '/timetable', label: 'Generate', icon: <FaRegCalendarPlus /> },
                { path: '/my-timetable', label: 'My Timetable', icon: <BiSolidCalendarStar /> },
                { path: '/admin-timetable', label: 'Manager', icon: <LuBookPlus /> },
                { path: '/exams', label: 'Exam Management', icon: <LuFileBadge2 /> },
            ],
            icon: <FaCalendarAlt />
        },
        {
            title: 'Campus Management',
            allowedRoles: [ROLES.ADMIN],
            items: [
                { path: '/resources', label: 'Resources & Labs', icon: <FiLayers /> },
                { path: '/discipline', label: 'Discipline Logs', icon: <AiOutlineFileProtect /> },
            ],
            icon: <FaUniversity />
        },
        {
            title: 'System',
            allowedRoles: [ROLES.ADMIN],
            items: [
                { path: '/audit-logs', label: 'Audit Logs', icon: <TbReportSearch /> },
                { path: '/system-config', label: 'System Configuration', icon: <HiWrenchScrewdriver /> },
            ],
            icon: <FaCogs />
        },
        {
            title: 'Attendance & Reports',
            allowedRoles: [ROLES.ADMIN, ROLES.TEACHER],
            items: [
                { path: '/attendance-history', label: 'Attendance History', icon: <LuHistory /> },
                { path: '/reports', label: 'Academic Reports', icon: <TbReport /> },
                { path: '/leave-requests', label: 'Leave Management', icon: <FiInbox /> },
                { path: '/notifications', label: 'Announcements', icon: <FiBell /> },
            ],
            icon: <BiSolidReport />
        },
        {
            title: 'Classroom',
            allowedRoles: [ROLES.ADMIN, ROLES.TEACHER],
            items: [
                { path: '/assignments', label: 'Assignments', icon: <BiTask /> },
                { path: '/grades', label: 'Grades & Results', icon: <FiAward /> },
            ],
            icon: <FaChalkboard />
        },
    ];

    const query = searchQuery.toLowerCase().trim();

    const showAccountGroup = query === '' ||
        "account".includes(query) ||
        "chat".includes(query) ||
        "settings".includes(query) ||
        "privacy policy".includes(query) ||
        "terms of service".includes(query);

    const filteredGroups = navGroups.map(group => {
        if (group.allowedRoles && !group.allowedRoles.includes(activeRole)) return null;

        let items = group.items;
        const role = activeRole?.toLowerCase();

        if (role === ROLES.TEACHER.toLowerCase()) {
            items = items.filter(item =>
                !['/teachers', '/students', '/subjects', '/sections', '/constraints',
                    '/timetable', '/admin-timetable', '/attendance', '/view-timetable',
                    '/reports', '/exams', '/audit-logs', '/resources', '/discipline',
                    '/system-config'].includes(item.path)
            );
        } else if (role === ROLES.ADMIN.toLowerCase()) {
            items = items.filter(item =>
                !['/my-timetable', '/teacher-attendance', '/assignments', '/grades'].includes(item.path)
            );
        }

        if (query !== '') {
            const groupMatches = group.title.toLowerCase().includes(query);
            items = items.filter(item =>
                groupMatches || item.label.toLowerCase().includes(query)
            );
        }

        if (items.length === 0) return null;
        return { ...group, items };
    }).filter(Boolean);

    const handleMobileClose = () => {
        setSearchQuery('');
        if (window.innerWidth <= 900) onClose();
    };

    const executeSearch = () => {
        if (query === '') return;
        let firstPath = null;
        if (filteredGroups.length > 0 && filteredGroups[0].items.length > 0) {
            firstPath = filteredGroups[0].items[0].path;
        } else if (showAccountGroup) {
            if ("chat".includes(query)) firstPath = '/chat';
            else if ("settings".includes(query)) firstPath = '/settings';
            else if ("privacy policy".includes(query)) firstPath = '/privacy-policies';
            else if ("terms of service".includes(query)) firstPath = '/terms-of-services';
            else firstPath = '/chat';
        }
        if (firstPath) { navigate(firstPath); handleMobileClose(); }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); executeSearch(); }
        if (e.key === 'Escape') setSearchQuery('');
    };

    const handleSearchIconClick = () => {
        if (isCollapsed) {
            toggleCollapse();
            setTimeout(() => { searchInputRef.current?.focus(); }, 300);
        } else {
            if (query !== '') executeSearch();
            else searchInputRef.current?.focus();
        }
    };

    return (
        <>
            {/* Backdrop */}
            <button
                className={`sidebar-backdrop ${isOpen ? 'sidebar-backdrop-visible' : ''}`}
                onClick={onClose}
                aria-label="Close navigation menu"
                tabIndex={isOpen ? 0 : -1}
            />

            <aside
                className={`sidebar ${isOpen ? 'sidebar-open' : ''} ${isCollapsed ? 'sidebar-collapsed' : ''}`}
                role="navigation"
                aria-label="Main navigation"
                data-tour="sidebar-nav"
            >
                {/* Background effects for pviolet metal look */}
                <div className="gc-hero-mesh"></div>
                <div className="gc-hero-grid"></div>
                {/* Mobile close */}
                <button className="sidebar-mobile-close" onClick={onClose} aria-label="Close menu">
                    <FiX />
                </button>

                {/* ── Header ── */}
                <div className="sidebar-header">
                    <div className="sidebar-logo">
                        <img
                            src="/Landscape_Icon.png"
                            alt="EduSync Logo"
                            className="sidebar-logo-img"
                        />
                        <div className="sidebar-brand-text">
                            <h1>{platformName}</h1>
                            <p className="sidebar-tagline">Smart Attendance</p>
                        </div>
                    </div>
                </div>

                {/* ── Search ── */}
                <div className="sidebar-search-container" data-tour="global-search">
                    {isCollapsed ? (
                        <div
                            className="sidebar-search-icon-wrapper"
                            aria-label="Open search"
                            role="button"
                            tabIndex={0}
                            onClick={handleSearchIconClick}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearchIconClick()}
                        >
                            <ImSearch className="sidebar-search-icon" aria-hidden="true" />
                        </div>
                    ) : (
                        <div className="sidebar-search-input-wrapper">
                            <ImSearch
                                title="Search or press Enter"
                                className="sidebar-search-action-icon left"
                                aria-label="Search"
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (query !== '') executeSearch();
                                    else searchInputRef.current?.focus();
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.stopPropagation();
                                        if (query !== '') executeSearch();
                                        else searchInputRef.current?.focus();
                                    }
                                }}
                            />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="sidebar-search-input"
                                placeholder="Search pages…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                aria-label="Search navigation"
                            />
                            {searchQuery.trim() !== '' && (
                                <FiX
                                    title="Clear (Esc)"
                                    className="sidebar-search-action-icon right"
                                    aria-label="Clear search"
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSearchQuery('');
                                        searchInputRef.current?.focus();
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.stopPropagation();
                                            setSearchQuery('');
                                            searchInputRef.current?.focus();
                                        }
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* ── Nav ── */}
                <nav className="sidebar-nav">
                    {filteredGroups.map((group, idx) => (
                        <details
                            className="sidebar-group"
                            key={group.title}
                            {...(idx === 0 ? { 'data-tour': 'dropdown-menu' } : {})}
                        >
                            <summary className={`sidebar-group-title ${isCollapsed ? 'noDisplay' : ''}`}>
                                <span className="sidebar-group-icon" title={group.title} aria-hidden="true">
                                    {group.icon}
                                </span>
                                {!isCollapsed && (
                                    <span className="summary-text">{group.title}</span>
                                )}
                                {/* Tooltip visible only when collapsed */}
                                {isCollapsed && (
                                    <span className="sidebar-tooltip-group">{group.title}</span>
                                )}
                            </summary>

                            <div className="sidebar-dropdown">
                                {group.items.map((item) => (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        onClick={handleMobileClose}
                                        className={({ isActive }) =>
                                            `sidebar-link${isActive ? ' sidebar-link-active' : ''}`
                                        }
                                        title={isCollapsed ? item.label : ''}
                                        aria-label={item.label}
                                        {...(item.path === '/instructions' ? { 'data-tour': 'instructions-link' } : {})}
                                    >
                                        <span className="sidebar-link-icon" aria-hidden="true">
                                            {item.icon}
                                        </span>
                                        {!isCollapsed && (
                                            <span className="sidebar-link-text">{item.label}</span>
                                        )}
                                        {isCollapsed && (
                                            <span className="sidebar-tooltip">{item.label}</span>
                                        )}
                                    </NavLink>
                                ))}
                            </div>
                            <div className="sidebar-divider" />
                        </details>
                    ))}

                    {/* No results */}
                    {filteredGroups.length === 0 && !showAccountGroup && (
                        <div className="sb-no-results">
                            Nothing found for "{searchQuery}"
                        </div>
                    )}

                    {/* Account group */}
                    {showAccountGroup && (
                        <details className="sidebar-group" data-tour="account-settings">
                            <summary className={`sidebar-group-title ${isCollapsed ? 'noDisplay' : ''}`}>
                                <span className="sidebar-group-icon" title="Account" aria-hidden="true">
                                    <MdAccountCircle />
                                </span>
                                {!isCollapsed && <span className="summary-text">Account</span>}
                                {isCollapsed && (
                                    <span className="sidebar-tooltip-group">Account</span>
                                )}
                            </summary>

                            <div className="sidebar-dropdown">
                                {[
                                    { path: '/chat', label: 'Chat', icon: <IoChatbubbleEllipsesOutline /> },
                                    { path: '/settings', label: 'Settings', icon: <FiSettings /> },
                                    { path: '/privacy-policies', label: 'Privacy Policy', icon: <MdOutlinePrivacyTip /> },
                                    { path: '/terms-of-services', label: 'Terms of Service', icon: <AiTwotoneContainer /> },
                                ].map((item) => (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        onClick={handleMobileClose}
                                        className={({ isActive }) =>
                                            `sidebar-link${isActive ? ' sidebar-link-active' : ''}`
                                        }
                                        title={isCollapsed ? item.label : ''}
                                        aria-label={item.label}
                                    >
                                        <span className="sidebar-link-icon" aria-hidden="true">{item.icon}</span>
                                        {!isCollapsed && <span className="sidebar-link-text">{item.label}</span>}
                                        {isCollapsed && <span className="sidebar-tooltip">{item.label}</span>}
                                    </NavLink>
                                ))}
                            </div>
                            <div className="sidebar-divider" />
                        </details>
                    )}
                </nav>

                {/* ── Footer ── */}
                <div className="sidebar-footer">
                    {/* Collapse toggle */}
                    <button
                        className="sidebar-toggle-btn"
                        onClick={toggleCollapse}
                        data-tour="sidebar-collapse"
                        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        style={{ alignSelf: isCollapsed ? 'center' : 'flex-end' }}
                    >
                        <FiChevronsLeft className={isCollapsed ? 'rot-collapse' : ''} />
                        {!isCollapsed && <span className="sidebar-toggle-text">Collapse</span>}
                    </button>

                    {/* Theme */}
                    <button
                        className="theme-toggle"
                        onClick={toggleTheme}
                        data-tour="theme-toggle"
                        aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                        title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                    >
                        <span className="theme-toggle-icon" aria-hidden="true">
                            {theme === 'light' ? <FiMoon /> : <FiSun />}
                        </span>
                        {!isCollapsed && <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>}
                        {isCollapsed && (
                            <span className="sidebar-tooltip">
                                {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                            </span>
                        )}
                    </button>

                    {/* User */}
                    <div
                        className="sidebar-user"
                        title={isCollapsed ? `${userName || 'Admin'} · ${formattedRole}` : ''}
                        data-tour="user-identity"
                    >
                        <div className="sidebar-user-avatar" aria-hidden="true">
                            {userName ? userName.charAt(0).toUpperCase() : 'A'}
                        </div>
                        {!isCollapsed && (
                            <div className="sidebar-user-info">
                                <div className="sidebar-user-name">{userName || 'Admin'}</div>
                                <div className="sidebar-user-role">{formattedRole}</div>
                            </div>
                        )}
                        {isCollapsed && (
                            <span className="sidebar-tooltip">
                                {userName || 'Admin'}
                                <br />
                                <span style={{ opacity: 0.6, fontSize: '0.72rem' }}>{formattedRole}</span>
                            </span>
                        )}
                    </div>

                    {/* Logout */}
                    <button
                        className="sidebar-logout"
                        data-tour="logout"
                        onClick={handleLogoutClick}
                        aria-label="Logout"
                        title={isCollapsed ? 'Logout' : ''}
                    >
                        <FiLogOut aria-hidden="true" />
                        {!isCollapsed && <span>Logout</span>}
                        {isCollapsed && <span className="sidebar-tooltip">Logout</span>}
                    </button>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;



